import BusinessCenterOutlinedIcon from '@mui/icons-material/BusinessCenterOutlined';
import BusinessOutlinedIcon from '@mui/icons-material/BusinessOutlined';
import CalendarMonthOutlinedIcon from '@mui/icons-material/CalendarMonthOutlined';
import KeyboardArrowDownOutlinedIcon from '@mui/icons-material/KeyboardArrowDownOutlined';
import { Card, Grid, Stack, Link, List, ListItemButton } from '@mui/material';
import { DataGrid, GridColDef, GridSortModel, GridColumnVisibilityModel } from '@mui/x-data-grid';
import dayjs from 'dayjs';
import { isEmpty } from 'lodash-es';
import { useCallback, useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  ClosureDTO,
  getClosures,
  InvoiceStatus,
  invoiceStatusMap,
  IOurEntity,
  ourEntityAllList,
  getPlacementStatusList,
  saveColumnSettings,
  ColumnSettingsDTO,
  ColumnSettings,
  getColumnSettings,
} from 'wisestep-api';
import {
  ControlledTextInput,
  WsButton,
  WsDatePicker,
  WsFilterDialog,
  formattedDate,
  WsTypography,
  WsTypoVariants,
  WsColors,
  WsElementWithPopOver,
  WsIconMoreVer,
  WsTypoColors,
  getDateDifferenceInDay,
  Item,
  WsIconAssignCandidate,
  WsIconText,
} from 'wisestep-base';

import { ClientAutocomplete } from '../../../components/ClientAutocomplete';
import WsCheckmarkSelect from '../../../components/WsCheckmarkSelect';
import WsMultiSelectSmartFilter from '../../../components/WsMultiSelectSmartFilter';
import { useWorkspaceContext } from '../../../context/WorkspaceContext';
import {
  clientsAmplitudeEvents,
  ClintPageBreadCrumb,
} from '../../../features/clientDetails/helper/clientDetailsHelper';
import PopoverOnClick from '../../../features/clientListingPage/components/PopoverOnClick';
import { getCommonGridStyles } from '../../../features/clientListingPage/helper/ClientListingHelper';
import { ClosureAction, useClosureAuthz } from '../../../hooks/useClosureAuthz';
import { getCurrencySymbol } from '../../../utils';
import getEncodedQueryParams from '../../../utils/getEncodedQueryParams';
import { CustomToolbar } from '../../invoicing/components/DataGridCustomToolBar';
import { ClosureFiltersForm, mapClosureFiltersToDTO } from '../helper/ClosureHelper';

const defaultValues: ClosureFiltersForm = {
  candidateSearch: null,
  ourEntities: [],
  clients: [],
};

const optionsData = new Map();

interface IClosureDataTable {
  isChild?: boolean;
  isClientPlacement?: boolean;
  clientId?: string;
}

const ClosureDataTable = ({ isClientPlacement, clientId }: IClosureDataTable) => {
  const form = useForm<ClosureFiltersForm>({ defaultValues });
  const [closureListData, setClosureListData] = useState<ClosureDTO[]>([]);
  const [settings, setSettings] = useState<GridColumnVisibilityModel | null>(null);
  const [pageNo, setPageNo] = useState<number>(0);
  const [pageSize, setPageSize] = useState<number>(25);
  const [totalRecord, setTotalRecord] = useState<number>(0);
  const [ourEntities, setOurEntities] = useState<IOurEntity[]>([]);
  const fromJoiningDate = form.watch('fromJoiningDate');
  const toJoiningDate = form.watch('toJoiningDate');
  const fromJoiningDateString = String(fromJoiningDate);
  const toJoiningDateString = String(toJoiningDate);
  const [sortModel, setSortModel] = useState<GridSortModel>([{ field: 'cand.name', sort: 'asc' }]);
  const navigate = useNavigate();
  const tableData: Record<string, unknown>[] = [];
  const showEditClosure = useClosureAuthz(ClosureAction.EDIT_CLOSURE);
  const isAuthorizedAddClosure = useClosureAuthz(ClosureAction.ADD_CLOSURE_ADDITION);
  const workspace = useWorkspaceContext();
  const navigateToAddClosure = (placementType: string) => {
    clientsAmplitudeEvents(
      'client_details_placement_add',
      'Client Details Placements Add',
      workspace
    );
    navigate('/placements/createoptions', { state: { placementType: placementType } });
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        const res = await getColumnSettings(ColumnSettings.ORAGANISATION_ENTITY);
        setSettings(res.data.settings);
      } catch (error) {
        setSettings({
          businessUnit: false,
          'c.commission_value': false,
          created_on: true,
          'c.joining_date': true,
          'c.offer_date': false,
          'c.sell_rate_amount': false,
          'cand.email': false,
          'cand.name': true,
          'cl.name': true,
          candidateContact: false,
          candidate_location: false,
          edit: true,
          empGuaranteeStatus: true,
          'i.invoice_status': true,
          'oe.name': true,
          ' p.title ': true,
          'ps.placementStatusId': true,
        });
      }
    };
    if (settings === null) {
      fetchData();
    }
  }, [settings]);

  const onColumnChange = async (data: GridColumnVisibilityModel) => {
    const settingsPayload: ColumnSettingsDTO = {
      settings: data,
      tableKey: ColumnSettings.ORAGANISATION_ENTITY,
    };
    await saveColumnSettings(settingsPayload);
  };

  const columnsName: GridColDef[] = [
    // {
    //   field: 'id',
    //   headerName: 'id',
    //   hide: true,
    // },
    {
      field: 'cand.name',
      headerName: 'Candidate Name',
      flex: 1,
      renderCell: (params) => (
        <>
          {isClientPlacement ? (
            <Link underline="hover" href={`/placements/${params.id}`} target="_blank">
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                bold
                cursor="pointer"
                display="inline"
                color={WsTypoColors.PRIMARY}
              >
                {params.row['cand.name']}
              </WsTypography>
            </Link>
          ) : (
            <Link
              component={RouterLink}
              sx={{ display: 'flex' }}
              underline="hover"
              to={`${params.id}`}
            >
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                bold
                cursor="pointer"
                display="inline"
                color={WsTypoColors.PRIMARY}
              >
                {params.row['cand.name']}
              </WsTypography>
            </Link>
          )}
        </>
      ),
    },
    {
      field: 'candidateContact',
      headerName: 'Candidate Contact',
      flex: 0.8,
    },
    {
      field: 'candidate_location',
      headerName: 'Candidate Location',
      flex: 0.8,
    },
    {
      field: 'cand.email',
      headerName: 'Candidate Email',
      flex: 0.8,
    },
    {
      field: 'businessUnit',
      headerName: 'Business Unit',
      flex: 0.8,
    },
    {
      field: 'ps.placementStatusId',
      headerName: 'Placement Status',
      flex: 1.6,
      sortable: false,
      renderCell: (params) => {
        return (
          <>
            <WsTypography bold cursor="pointer">
              {optionsData.get(params.row['ps.placementStatusId'])}
            </WsTypography>
          </>
        );
      },
    },
    {
      field: 'cl.name',
      headerName: 'Client',
      flex: 0.9,
      renderCell: (params) => (
        <>
          {isClientPlacement ? (
            <WsTypography
              variant={WsTypoVariants.SUB_TITLE}
              cursor="pointer"
              color={WsTypoColors.BLACK}
            >
              {params.row['cl.name']}
            </WsTypography>
          ) : (
            <Link
              component={RouterLink}
              underline="hover"
              to={`/client-details?isChild=${params.row.isParent}&tabIndex=${
                params.row.isParent ? 5 : 4
              }&name=${params.row.parentName}&clientId=${
                params.row.clientId
              }&breadCrumbList=${getEncodedQueryParams(ClintPageBreadCrumb['CLIENTS'])}`}
            >
              <WsTypography
                variant={WsTypoVariants.SUB_TITLE}
                cursor="pointer"
                color={WsTypoColors.PRIMARY}
              >
                {params.row['cl.name']}
              </WsTypography>
            </Link>
          )}
        </>
      ),
    },
    {
      field: 'oe.name',
      headerName: 'Organisation Entity',
      flex: 1,
      renderCell: (params) => (
        <Link
          component={RouterLink}
          sx={{ display: 'flex' }}
          underline="hover"
          to={`/organisationEntity/${params.row.ourEntitiesId}/details`}
        >
          <WsTypography
            variant={WsTypoVariants.SUB_TITLE}
            cursor="pointer"
            color={WsTypoColors.PRIMARY}
          >
            {params.row['oe.name']}
          </WsTypography>
        </Link>
      ),
    },
    {
      field: 'p.title',
      headerName: 'Job Title',
      flex: 0.8,
    },
    {
      field: 'c.joining_date',
      headerName: 'Joining Date',
      flex: 1,
    },
    {
      field: 'c.offer_date',
      headerName: 'Offer Date',
      flex: 1,
    },
    {
      field: 'empGuaranteeStatus',
      headerName: 'Employment guarantee status',
      flex: 1.6,
      sortable: false,
      renderCell: (params) => {
        let status = false;
        const endDate = params.row['c.contract_guarantee_end_date'];
        const currentDate = new Date();
        if (getDateDifferenceInDay(endDate, currentDate.toDateString()) < 0) {
          status = true;
        }
        return (
          <>
            <WsTypography bold cursor="pointer">
              {status ? 'Success' : 'In-Progress'}
            </WsTypography>
          </>
        );
      },
    },
    {
      field: 'i.invoice_status',
      headerName: 'Invoice Status',
      flex: 0.9,
      renderCell: (params) => (params.value ? invoiceStatusMap[InvoiceStatus[params.value]] : ''),
    },

    {
      field: 'c.commission_value',
      headerName: 'Commission Value',
      flex: 0.8,
    },
    {
      field: 'c.sell_rate_amount',
      headerName: 'Compensation Amount',
      flex: 0.8,
    },
    {
      field: 'created_on',
      headerName: 'Created On',
      flex: 1,
    },
  ];

  if (showEditClosure) {
    columnsName.push({
      field: 'edit',
      headerName: '',
      sortable: false,
      renderCell: (params) => (
        <>
          {isClientPlacement ? (
            <PopoverOnClick element={<WsIconMoreVer />}>
              <Stack p={2} columnGap={1}>
                <Link
                  underline="hover"
                  href={`/placements/${params.id}/edit/0`}
                  onClick={() =>
                    clientsAmplitudeEvents(
                      'client_details_placement_edit',
                      'Client Details Placements Edit',
                      workspace
                    )
                  }
                >
                  <WsTypography variant={WsTypoVariants.SUB_TITLE} cursor="pointer">
                    Edit
                  </WsTypography>
                </Link>
              </Stack>
            </PopoverOnClick>
          ) : (
            <PopoverOnClick element={<WsIconMoreVer />}>
              <Stack p={2} columnGap={1}>
                <WsTypography
                  variant={WsTypoVariants.SUB_TITLE}
                  cursor="pointer"
                  onClick={() => {
                    clientsAmplitudeEvents(
                      'client_details_placement_edit',
                      'Client Details Placements Edit',
                      workspace
                    );
                    navigate(`${params.id}/edit/0`);
                  }}
                >
                  Edit
                </WsTypography>
              </Stack>
            </PopoverOnClick>
          )}
        </>
      ),
    });
  }

  closureListData.forEach((closureListData, index) => {
    tableData.push({
      id: closureListData.closureId,
      'cand.name': closureListData.candidateName,
      'p.title': closureListData.jobTitle,
      'cl.name': closureListData.clientName,
      'oe.name': closureListData.ourEntityName,
      'i.invoice_status': closureListData.invoiceStatus,
      'i.invoice_code': closureListData.invoiceReference,
      'c.joining_date':
        closureListData?.joiningDate != undefined
          ? dayjs(parseInt(closureListData?.joiningDate)).format('DD-MMM-YY')
          : '',
      'c.contract_guarantee_end_date': closureListData.employmentGuaranteeEndDate,
      'ps.placementStatusId': closureListData.placementStatusId,
      created_on:
        closureListData?.createdOn != undefined
          ? dayjs(parseInt(closureListData?.createdOn)).format('DD-MMM-YY')
          : '',
      'c.offer_date':
        closureListData?.offerDate != undefined
          ? dayjs(parseInt(closureListData?.offerDate)).format('DD-MMM-YY')
          : '',
      'cand.email': closureListData.candidateEmail,
      'c.commission_value': `${
        closureListData.currencySymbol ? getCurrencySymbol(closureListData.currencySymbol) : ''
      } ${closureListData.commissionValue}`,
      'c.sell_rate_amount': `${
        closureListData.currencySymbol ? getCurrencySymbol(closureListData.currencySymbol) : ''
      } ${closureListData.compensationAmount}`,
      'fa.fee': closureListData.feeArrangementAmount,
      businessUnit: '',
      candidateContact: closureListData?.candidatePhone,
      candidate_location:
        closureListData.candidateLocation !== undefined
          ? `${closureListData.candidateLocation?.city}, ${closureListData.candidateLocation?.state}`
          : '',
      empGuaranteeStatus: 0,
      index: index,
      clientId: closureListData.clientId,
      ourEntitiesId: closureListData.ourEntityId,
      parentName: closureListData.parentName,
      isParent: closureListData.isParent,
    });
  });

  const selectedJoiningDates =
    fromJoiningDate && toJoiningDate
      ? `${formattedDate(fromJoiningDateString)} to ${formattedDate(toJoiningDateString)}`
      : fromJoiningDate
      ? `From ${formattedDate(fromJoiningDateString)}`
      : toJoiningDate
      ? `Till ${formattedDate(toJoiningDateString)}`
      : 'All';

  const updateClosures = useCallback(
    async (filters) => {
      const response1 = await getPlacementStatusList();
      response1.data.forEach((data) => optionsData.set(data.id, data.placementStatusName));
      isClientPlacement && filters.clients.push({ id: clientId });
      const dto = mapClosureFiltersToDTO(filters);
      const sortBy = !isEmpty(sortModel) ? sortModel[0].field : undefined;
      const sortOrder = !isEmpty(sortModel) ? sortModel[0].sort : undefined;
      setClosureListData([]);
      const response = await getClosures({
        ...dto,
        page: pageNo,
        size: pageSize,
        sortBy: sortBy,
        sortOrder: sortOrder,
      });
      setTotalRecord(response.data.totalElements);
      setClosureListData(response.data.content);
    },
    [clientId, isClientPlacement, pageNo, pageSize, sortModel]
  );

  const fetchAll = async () => {
    const response = await getPlacementStatusList();
    response.data.forEach((data) => optionsData.set(data.id, data.placementStatusName));
  };

  useEffect(() => {
    (async () => {
      await updateClosures(form.getValues());
    })();
    fetchAll();
  }, [form, pageNo, updateClosures]);

  useEffect(() => {
    async () => {
      const response = await ourEntityAllList();
      setOurEntities(response.data.data);
    };
  }, []);

  const onSearch = (data) => {
    updateClosures(data);
  };

  const AddPlacementButton = () => (
    <WsElementWithPopOver
      element={
        <WsButton variant="contained" size="medium" endIcon={<KeyboardArrowDownOutlinedIcon />}>
          Add Placement
        </WsButton>
      }
    >
      <List dense={true} sx={{ pl: 1 }}>
        <ListItemButton onClick={() => navigateToAddClosure('PERMANENT')}>
          <WsIconText
            text="Permanent Placement"
            icon={<WsIconAssignCandidate />}
            textCursor="pointer"
            textSize={WsTypoVariants.BODY1}
          />
        </ListItemButton>
      </List>
    </WsElementWithPopOver>
  );

  return (
    <>
      {isClientPlacement && (workspace.owns || isAuthorizedAddClosure) && (
        <Item sx={{ display: 'flex', justifyContent: 'right' }}>
          <AddPlacementButton />
        </Item>
      )}
      <FormProvider {...form}>
        <Card elevation={0} sx={{ p: 2 }}>
          <form onSubmit={form.handleSubmit(onSearch)}>
            <Grid container rowSpacing={2} columnSpacing={1}>
              <Grid item xs={10}>
                <ControlledTextInput
                  fullWidth
                  name="candidateSearch"
                  placeholder="Search Candidate..."
                  size="small"
                />
              </Grid>
              <Grid item xs={1}>
                <WsButton
                  fullWidth
                  variant="text"
                  size="large"
                  type="reset"
                  onClick={() => {
                    form.reset(defaultValues);
                    onSearch(defaultValues);
                  }}
                >
                  Reset
                </WsButton>
              </Grid>
              <Grid item xs={1}>
                <WsButton fullWidth size="large" type="submit">
                  Search
                </WsButton>
              </Grid>
              <Grid item xs={2.5}>
                <WsMultiSelectSmartFilter
                  label="Our Entity"
                  name="ourEntities"
                  icon={<BusinessCenterOutlinedIcon />}
                >
                  <WsCheckmarkSelect
                    name="ourEntities"
                    options={ourEntities}
                    getLabel={(option) => option.Name}
                  />
                </WsMultiSelectSmartFilter>
              </Grid>
              {!(isClientPlacement == true) && (
                <Grid item xs={2.5}>
                  <WsMultiSelectSmartFilter
                    label="Client Name"
                    name="clients"
                    icon={<BusinessOutlinedIcon />}
                  >
                    <ClientAutocomplete name="clients" multiple />
                  </WsMultiSelectSmartFilter>
                </Grid>
              )}
              <Grid item xs={2.5}>
                <WsFilterDialog
                  label="Joining Date"
                  name={selectedJoiningDates}
                  icon={<CalendarMonthOutlinedIcon />}
                >
                  <Stack spacing={2} direction="row" py={2} px={1}>
                    <WsDatePicker name="fromJoiningDate" label="From Date" />
                    <WsDatePicker name="toJoiningDate" label="To Date" minDate={fromJoiningDate} />
                  </Stack>
                </WsFilterDialog>
              </Grid>
            </Grid>
            {settings != null && (
              <Grid xs={12} bgcolor={WsColors.White} mt={1}>
                <DataGrid
                  autoHeight={true}
                  components={{ Toolbar: CustomToolbar }}
                  columns={columnsName}
                  rows={tableData}
                  onPageChange={(page) => setPageNo(page)}
                  page={pageNo}
                  paginationMode="server"
                  pageSize={pageSize}
                  rowCount={totalRecord}
                  rowsPerPageOptions={[25, 50, 75, 100]}
                  onPageSizeChange={(newPageSize) => setPageSize(newPageSize)}
                  sortModel={sortModel}
                  onSortModelChange={(newSortModel) => {
                    setPageNo(0);
                    setSortModel(newSortModel);
                  }}
                  columnVisibilityModel={settings}
                  onColumnVisibilityModelChange={(data) => {
                    onColumnChange(data);
                    setSettings(data);
                  }}
                  sortingMode="server"
                  getRowHeight={() => 'auto'}
                  sx={getCommonGridStyles()}
                />
              </Grid>
            )}
          </form>
        </Card>
      </FormProvider>
    </>
  );
};

export default ClosureDataTable;
